import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Design-system Button. One `primary` per view (สมัคร / ส่ง / ต่อไป);
 * everything else is `ghost`. Minimum height 44px, focus ring is `focus`.
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded-ds-sm border-[1.5px] font-sans font-semibold',
    'transition-[background-color,box-shadow,color,border-color] duration-200',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
    'disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none',
  ].join(' '),
  {
    variants: {
      variant: {
        primary:
          'border-transparent bg-accent text-on-accent hover:bg-gold-highlight hover:shadow-glow-gold disabled:hover:bg-accent',
        ghost:
          'border-ink bg-transparent text-ink hover:bg-surface-raised disabled:hover:bg-transparent',
        outline:
          'border-ink bg-transparent text-ink hover:bg-surface-raised disabled:hover:bg-transparent',
        link: 'border-transparent bg-transparent text-ink underline-offset-4 hover:underline',
      },
      size: {
        default: 'min-h-11 px-6 text-base leading-6',
        sm: 'min-h-10 px-4 text-[15px] leading-6',
        lg: 'min-h-12 px-8 text-base leading-6',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
